import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { removeToast, Toast } from "../../../slices/toastSlice";
import styles from "./toast.module.scss";

const DISMISS_DELAY = 3500;
const EXIT_ANIMATION = 300;

const accentClasses: Record<Toast["type"], string> = {
  success: styles.accentSuccess,
  info: styles.accentInfo,
  warning: styles.accentWarning,
};

const iconClasses: Record<Toast["type"], string> = {
  success: styles.iconSuccess,
  info: styles.iconInfo,
  warning: styles.iconWarning,
};

const icons: Record<Toast["type"], React.ReactNode> = {
  success: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};

const ToastItem: React.FC<{ toast: Toast }> = ({ toast }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [exiting, setExiting] = useState(false);

  const startExit = useCallback(() => {
    setExiting(true);
    setTimeout(() => dispatch(removeToast(toast.id)), EXIT_ANIMATION);
  }, [dispatch, toast.id]);

  useEffect(() => {
    const timer = setTimeout(startExit, toast.duration ?? DISMISS_DELAY);
    return () => clearTimeout(timer);
  }, [startExit, toast.duration]);

  return (
    <div
      className={`${styles.toast} ${accentClasses[toast.type]} ${exiting ? styles.exiting : ""}`}
      role="status"
      aria-live="polite"
    >
      <div className={`${styles.icon} ${iconClasses[toast.type]}`}>
        {icons[toast.type]}
      </div>
      <div className={styles.message}>{toast.message}</div>
      <button className={styles.closeBtn} onClick={startExit} aria-label={t("common.close", "Close")}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

const ToastContainer: React.FC = () => {
  const toasts = useSelector((state: RootState) => state.toasts.toasts);
  const el = document.getElementById("toast-root");
  if (!el) return null;

  return ReactDOM.createPortal(
    <div className={styles.container}>
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>,
    el
  );
};

export default ToastContainer;
